import { visibleFields } from "../fields";
import type { FieldPack } from "../engine/types";
import { Card, Chip } from "./primitives";
import { Icon } from "./Icon";
import { Link, type Route } from "./router";

/**
 * The catalog: choosing a tool, not browsing an app store.
 *
 * Reads visibleFields() and nothing else — a new field pack appears here the
 * moment its file exists, which is the whole architectural claim made visible.
 */

export function Catalog({ navigate }: { navigate: (r: Route) => void }) {
  const fields = visibleFields();

  return (
    <div className="grid gap-8">
      {/* ─── Hero ─── */}
      <section className="md-enter pt-2 sm:pt-6">
        <p className="md-label-large mb-3 inline-flex items-center gap-1.5 rounded-full bg-tertiary-container px-3 py-1 text-on-tertiary-container">
          <Icon name="edit_note" size={16} filled />
          استمارة، مو مربع فاضي
        </p>
        <h1 className="md-display-small text-on-surface sm:md-display-medium">
          مو لازم تعرف تسأل — <span className="text-primary">احنا نسألك</span>.
        </h1>
        <p className="md-body-large mt-4 max-w-2xl text-on-surface-variant">
          أغلب الأدوات تحط قدامك مربع فاضي وتتركك تخمّن شنو تكتب. كلشي يعكسها: تختار المجال،
          ونحن نسألك الأسئلة اللي يسألها أهل الاختصاص — نوع النص، الجمهور، الغرض — وتطلع النتيجة
          مضبوطة من أول مرة.
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          <Chip tone="neutral" icon="check">
            الأسئلة نفسها هي الخبرة
          </Chip>
          <Chip tone="neutral" icon="check">
            تكدر تعدّل وتعيد التشغيل
          </Chip>
          <Chip tone="neutral" icon="check">
            ٣ تشغيلات يومياً بدون حساب
          </Chip>
        </div>
      </section>

      {/* ─── Fields ─── */}
      <section>
        <h2 className="md-title-large mb-3 flex items-center gap-2 text-on-surface">
          <Icon name="grid_view" size={22} className="text-on-surface-variant" />
          اختر المجال
        </h2>

        {fields.length === 0 ? (
          <Card variant="filled" className="p-6">
            <p className="md-body-large text-on-surface-variant">ما في أدوات متاحة حالياً.</p>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <FieldCard key={f.id} pack={f} navigate={navigate} />
            ))}
          </div>
        )}
      </section>

      {/* ─── How it works ─── */}
      <section>
        <h2 className="md-title-large mb-3 text-on-surface">شلون تشتغل؟</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { n: "١", icon: "touch_app", t: "اختر المجال", b: "ترجمة، قانون، تلخيص…" },
            { n: "٢", icon: "checklist", t: "جاوب على الأسئلة", b: "كل سؤال إله سبب — وكل واحد يغيّر النتيجة." },
            { n: "٣", icon: "auto_awesome", t: "خذ النتيجة", b: "وإذا ما عجبتك، عدّل جواب واحد وأعِد التشغيل." },
          ].map((s) => (
            <Card key={s.n} variant="filled" className="p-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="md-label-large grid h-7 w-7 place-items-center rounded-full bg-primary text-on-primary">
                  <bdi>{s.n}</bdi>
                </span>
                <Icon name={s.icon} size={20} className="text-on-surface-variant" />
              </div>
              <h3 className="md-title-small text-on-surface">{s.t}</h3>
              <p className="md-body-small mt-1 text-on-surface-variant">{s.b}</p>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function FieldCard({ pack, navigate }: { pack: FieldPack; navigate: (r: Route) => void }) {
  return (
    <Link
      to={{ name: "field", fieldId: pack.id }}
      navigate={navigate}
      className="md-state group block rounded-lg border border-outline-variant bg-surface-lowest p-5 transition-shadow hover:shadow-e2"
    >
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-primary-container text-on-primary-container transition-colors group-hover:bg-primary group-hover:text-on-primary">
          <Icon name={pack.icon} size={26} filled />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="md-title-large text-on-surface">{pack.title}</h3>
            {pack.retrieval ? (
              <Chip tone="tertiary" icon="menu_book">
                بمصادر
              </Chip>
            ) : null}
          </div>
          <p className="md-body-medium mt-1 text-on-surface-variant">{pack.blurb}</p>

          <div className="mt-3 flex items-center gap-2">
            <Chip tone="neutral" icon="bolt">
              <bdi>{pack.cost}</bdi> {pack.cost === 1 ? "وحدة" : "وحدات"}
            </Chip>
            <Chip tone="neutral" icon="help_outline">
              <bdi>{pack.inputs.length}</bdi> أسئلة
            </Chip>
            <span className="ms-auto text-primary opacity-0 transition-opacity group-hover:opacity-100">
              {/* RTL: "forward" points left. */}
              <Icon name="arrow_back" size={20} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
